import { createContext, useContext, useState } from 'react';

import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { createForm, useCreateFormStore } from '@components/form';
import { useDeleteModal } from '@components/generic-modals';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOutput } from '@components/icons/output';
import { IconRemove } from '@components/icons/remove';
import { IconSend } from '@components/icons/send';
import { Menu } from '@components/menu';
import { MenuItem } from '@components/menu/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import {
  useDeleteIntegrationEndpoint,
  useUpdateIntegrationEndpoint,
  useUpdateIntegrationEndpointDefaultFiles,
  useUpdateIntegrationEndpointExampleFiles,
} from 'api/integrations/hooks';
import { IntegrationEndpoint, IntegrationEndpointVariable } from 'api/integrations/requests';

import {
  FieldTable,
  MethodTag,
  generateTemplateName,
  getFormEndpointVariables,
  getVariableCount,
  methodFancyContainerColorMap,
} from './common';
import { useDuplicateIntegrationEndpointModal } from './modals/duplicate-endpoint';
import { useTestRequestModal } from './modals/test-request';
import { ResponseValueCard } from './response-values';
import { useIntegrationEndpointCreateSidebar } from './sidebars/add-edit-endpoint';

type EndpointCardProps = {
  endpoint: IntegrationEndpoint<string>;
};

export const EndpointCard = ({ endpoint }: EndpointCardProps) => {
  const { t } = useScopedTranslate(locale);
  const [responseValuesCollapsed, setResponseValuesCollapsed] = useState(true);
  const integrationId = endpoint.integration;
  const showEditSidebar = useIntegrationEndpointCreateSidebar();
  const showDeleteModal = useDeleteModal();
  const showDuplicateModal = useDuplicateIntegrationEndpointModal();
  const deleteMutation = useDeleteIntegrationEndpoint(endpoint.integration, endpoint.shortId);
  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      space={4}
      variant="white"
      key={endpoint.shortId}
      renderCollapse={() => (
        <Stack space={2}>
          <RequestValuesCard endpoint={endpoint} />
          <ResponseValueCard
            endpoint={endpoint}
            defaultCollapsed={responseValuesCollapsed}
            onVisibilityToggle={setResponseValuesCollapsed}
            key={Object.keys(endpoint.responseValues ?? {}).length}
          />
        </Stack>
      )}
    >
      <Stack>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack space={4} orientation="horizontal" align="center">
            <IconFancyContainer
              variant={methodFancyContainerColorMap[endpoint.method]}
              icon={<IconApi />}
            />
            <div>
              <Text text={endpoint.name} font="semibold14" />
              <Text
                text={endpoint.url}
                style={{
                  ...lineClamp('-webkit-inline-box', 1, true),
                  paddingRight: pxToRem(6),
                }}
                font="regular14"
                color="textHelper"
              />
            </div>
          </Stack>
          <Stack space={4} orientation="horizontal" align="center">
            <MethodTag method={endpoint.method} />
            <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
              <MenuItem
                icon={<IconEdit />}
                label={t('core.edit')}
                onSelect={() => showEditSidebar({ integrationId, endpoint })}
              />
              <MenuItem
                icon={<IconDuplicate />}
                label={t('core.duplicate')}
                onSelect={() => showDuplicateModal({ endpoint })}
              />
              <Menu.Separator />
              <MenuItem
                icon={<IconRemove />}
                label={t('deleteEndpoint')}
                variant="danger"
                onSelect={() =>
                  showDeleteModal({
                    title: t('deleteEndpoint'),
                    description: t('areYouSureDelete'),
                    buttonText: t('deleteEndpoint'),
                    variant: 'LARGE',
                    hideCannotBeUndone: true,
                    onConfirm: () => deleteMutation.mutate(),
                  })
                }
              />
            </Menu>
          </Stack>
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

type EndpointType = IntegrationEndpoint<string> | IntegrationEndpoint;
export const endpointContext = createContext<EndpointType>({} as EndpointType);

type RequestValuesCardProps = {
  endpoint: IntegrationEndpoint<string>;
};

type Data = {};

const Form = createForm<Data>();

const getDefaultValueFiles = (
  variables: IntegrationEndpointVariable[],
  allVariables: IntegrationEndpointVariable[],
  formData: FormData
) => {
  variables.forEach((variable) => {
    if (variable.type === 'file') {
      const variableName = generateTemplateName(allVariables, variable);
      const value = variable.default_value;
      if (value instanceof File) {
        formData.append(variableName, value as File);
      }
      if (value === null) {
        formData.append(`${variableName}__remove`, '');
      }
      variable.default_value = '';
    }
    if (variable.type === 'array' && variable.template) {
      getDefaultValueFiles(variable.template, allVariables, formData);
    }
  });
};

export const getExampleValueFiles = (
  variables: IntegrationEndpointVariable[],
  allVariables: IntegrationEndpointVariable[],
  formData: FormData
) => {
  variables.forEach((variable) => {
    if (variable.type === 'file') {
      const variableName = generateTemplateName(allVariables, variable);
      const value = variable.example;
      if (value instanceof File) {
        formData.append(variableName, value as File);
      }
      if (value === null) {
        formData.append(`${variableName}__remove`, '');
      }
      variable.example = '';
    }
    if (variable.type === 'array' && variable.template) {
      getExampleValueFiles(variable.template, allVariables, formData);
    }
  });
};

export const RequestValuesCard = ({ endpoint }: RequestValuesCardProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const showTestRequestModal = useTestRequestModal();
  const updateMutation = useUpdateIntegrationEndpoint();
  const defaultFilesMutation = useUpdateIntegrationEndpointDefaultFiles();
  const exampleFileMutation = useUpdateIntegrationEndpointExampleFiles();

  const endpointVariables = endpoint.variables as IntegrationEndpointVariable[];
  const visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: true,
    visible: true,
    format: false,
  };

  const handleUpdateEndpoint = async () => {
    const variables = getFormEndpointVariables({ formStore, endpointVariables, visibleColumns });
    const defaultFiles = new FormData();
    getDefaultValueFiles(variables, variables, defaultFiles);
    const exampleFiles = new FormData();
    getExampleValueFiles(variables, variables, exampleFiles);

    await updateMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: {
        variables,
      },
    });

    if (visibleColumns.example && Array.from(exampleFiles.keys()).length > 0) {
      exampleFileMutation.mutateAsync({
        shortId: endpoint.shortId,
        data: exampleFiles,
      });
    }
    if (visibleColumns.default && Array.from(defaultFiles.keys()).length > 0) {
      defaultFilesMutation.mutateAsync({
        shortId: endpoint.shortId,
        data: defaultFiles,
      });
    }
  };
  const formActionPortalId = `${endpoint.shortId}-form-actions-portal`;

  return (
    <endpointContext.Provider value={endpoint}>
      <CollapseCard
        keepCollapsedInDom
        defaultCollapsed
        spaceX={4}
        spaceY={2}
        variant="gray"
        renderCollapse={() => (
          <Form store={formStore} onSubmit={handleUpdateEndpoint}>
            <FieldTable
              form={Form}
              endpointId={endpoint.shortId}
              endpointVariables={endpointVariables}
              visibleColumns={visibleColumns}
              requestType={endpoint.requestType}
            />
            {isDirty && (
              <Form.Actions portal={formActionPortalId}>
                <Stack orientation="horizontal" space={2} align="center">
                  <Form.Reset stopPropagation text={t('core.cancel')} variant="secondary" />
                  <Form.Submit
                    stopPropagation
                    isDisabled={updateMutation.isLoading}
                    text={t('core.save')}
                    variant="primary-blue"
                  />
                </Stack>
              </Form.Actions>
            )}
          </Form>
        )}
      >
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" align="center" space={2}>
            <IconOutput color="elementPurple50" />
            <Text text={t('requestValues')} font="semibold14" />
            <Text text="•" color="textHelper" />
            <Text text={getVariableCount(endpointVariables)} font="semibold14" />
          </Stack>
          <Stack orientation="horizontal" space={4} align="center">
            <Stack space={2} orientation="horizontal" align="center">
              {!isDirty && (
                <Button
                  stopPropagation
                  text={t('test')}
                  iconLeft={<IconSend />}
                  variant="ghost"
                  onClick={() => showTestRequestModal({ endpointId: endpoint.shortId })}
                />
              )}
              <div id={formActionPortalId} />
            </Stack>
          </Stack>
        </Stack>
      </CollapseCard>
    </endpointContext.Provider>
  );
};

const idSeparator = '---';

export const getFieldIds = (name: string) => {
  const fieldId = `${name}${idSeparator}`;
  return {
    typeId: `${fieldId}type` as keyof Data,
    optionsFromId: `${fieldId}options-from` as keyof Data,
    labelId: `${fieldId}label` as keyof Data,
    exampleId: `${fieldId}example` as keyof Data,
    defaultValueId: `${fieldId}default` as keyof Data,
    visibleId: `${fieldId}visible` as keyof Data,
    formatId: `${fieldId}format` as keyof Data,
    jqQueryId: `${fieldId}jqQuery` as keyof Data,
  };
};

type ValueFieldProps = {
  field: IntegrationEndpointVariable;
  form: typeof Form;
  id: keyof Data;
  defaultValue: any;
};

export const ExampleValueField = ({ field, form: Form, id, defaultValue }: ValueFieldProps) => {
  return (
    <ValueField id={id} field={field} form={Form} defaultValue={defaultValue} type="example" />
  );
};

type FileFieldProps = {
  field: Pick<IntegrationEndpointVariable, 'name' | 'example' | 'type'>;
  form: typeof Form;
  id: keyof Data;
};

const MultipartExampleFileField = ({ field, form: Form, id }: FileFieldProps) => {
  const endpoint = useContext(endpointContext);
  const exampleFile = endpoint.exampleFiles?.find((file) => file.variableName === field.name);
  const file = exampleFile
    ? { id: exampleFile.id, name: exampleFile.name, src: exampleFile.src }
    : undefined;
  const url = file ? new URL(file.src) : undefined;
  const key = `${url ? url.pathname.split('/').pop() : undefined}`;
  return (
    <Form.FileField
      id={id}
      key={key}
      label={field.name}
      hideLabel
      variant="small"
      defaultValue={file}
    />
  );
};

const MultipartDefaultFileField = ({ field, form: Form, id }: FileFieldProps) => {
  const endpoint = useContext(endpointContext);
  const defaultFile = endpoint.defaultFiles?.find((file) => file.variableName === field.name);
  const file = defaultFile
    ? { id: defaultFile.id, name: defaultFile.name, src: defaultFile.src }
    : undefined;
  const url = file ? new URL(file.src) : undefined;
  const key = `${url ? url.pathname.split('/').pop() : undefined}`;
  return (
    <Form.FileField
      id={id}
      key={key}
      label={field.name}
      hideLabel
      variant="small"
      defaultValue={file}
    />
  );
};

export const DefaultValueField = ({ field, form: Form, id, defaultValue }: ValueFieldProps) => {
  return (
    <ValueField id={id} field={field} form={Form} defaultValue={defaultValue} type="default" />
  );
};

const ValueField = ({
  field,
  form: Form,
  id,
  defaultValue,
  type,
}: ValueFieldProps & { type: 'example' | 'default' }) => {
  switch (field.type) {
    case 'number':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['number']}
          defaultValue={(defaultValue ?? '').toString()}
        />
      );
    case 'decimal':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['decimal']}
          defaultValue={(defaultValue ?? '').toString()}
        />
      );
    case 'select':
      return (
        <Form.Select
          id={id}
          label={field.name}
          options={field.options ?? []}
          hideLabel
          defaultValue={defaultValue}
        />
      );
    case 'date':
      return <Form.DatePicker id={id} label={field.name} hideLabel defaultValue={defaultValue} />;
    case 'email':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['email']}
          defaultValue={defaultValue ?? ''}
        />
      );
    case 'file':
      return type === 'default' ? (
        <MultipartDefaultFileField id={id} field={field} form={Form} />
      ) : (
        <MultipartExampleFileField id={id} field={field} form={Form} />
      );
    case 'boolean':
      return (
        <Form.Switch
          id={id}
          label={field.name}
          labelVisible={false}
          defaultValue={defaultValue ?? false}
        />
      );
    case 'array':
      return <></>;
    case 'paragraph':
    case 'text':
    default:
      return (
        <Form.TextField id={id} label={field.name} hideLabel defaultValue={defaultValue ?? ''} />
      );
  }
};

const locale = {
  en: {
    array: 'Array',
    field: 'Field',
    type: 'Type',
    label: 'Label',
    testRequest: 'Test request',
    test: 'Test',
    deleteEndpoint: 'Delete Endpoint',
    areYouSureDelete:
      'You are about to permanently delete this endpoint. The following will be affected:\n• All current configurations and response values\n• Journeys that rely on this endpoint\n• Travellers who are currently in progress\n\nOnce deleted, you can recreate this endpoint, but any lost data or configurations cannot be restored.',
    requestValues: 'Request values',
  },
};
